import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Robot, Sparkle, UploadSimple, ArrowCounterClockwise, CheckCircle,
  Warning, Table as TableIcon, CircleNotch, CaretLeft, ClockCounterClockwise, FileArrowUp, DownloadSimple,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { AiProgressBanner } from '@/components/AiProgressBanner';
import { formatDateTime } from '@/utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let _mid = 0;
const nextId = () => `m${Date.now()}-${_mid++}`;

/** AI-driven data import assistant that lives inside the Chat app.
 * Upload Excel/CSV/画像/PDF → AI maps columns → preview → 取り込む → 取り消す (undo). Admin/Manager only. */
export default function AiImportPanel({ onBack }) {
  const [config, setConfig] = useState(null);
  const [messages, setMessages] = useState([]);
  const [batches, setBatches] = useState([]);
  const [busy, setBusy] = useState(false);
  const [busyStartedAt, setBusyStartedAt] = useState(null);
  const [busyLabel, setBusyLabel] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);

  const loadBatches = () =>
    axios.get(`${API}/ai-import/batches`).then((r) => setBatches(r.data || [])).catch(() => {});

  useEffect(() => {
    axios.get(`${API}/ai-import/config`).then((r) => setConfig(r.data)).catch(() => {});
    loadBatches();
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const analyze = async (file) => {
    setBusy(true);
    setBusyStartedAt(new Date().toISOString());
    setBusyLabel('ファイルをAIで解析中…');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/ai-import/analyze`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMessages((m) => [...m, {
        id: nextId(), role: 'analysis', file, data: r.data,
        cache: { [r.data.target]: r.data }, consumed: false,
      }]);
    } catch (e) {
      const detail = e.response?.data?.detail || '解析に失敗しました';
      toast.error(detail);
      setMessages((m) => [...m, { id: nextId(), role: 'error', text: detail }]);
    } finally {
      setBusy(false);
      setBusyStartedAt(null);
      setBusyLabel(null);
    }
  };

  // Switching the target reuses a cached analysis (no repeat LLM call) when available.
  const changeTarget = async (msg, newTarget) => {
    const cached = msg.cache?.[newTarget];
    if (cached) {
      setMessages((m) => m.map((x) => (x.id === msg.id ? { ...x, data: cached } : x)));
      return;
    }
    setBusy(true);
    setBusyStartedAt(new Date().toISOString());
    setBusyLabel(`ターゲットを ${newTarget} に切り替えて再解析中…`);
    const fd = new FormData();
    fd.append('file', msg.file);
    fd.append('target', newTarget);
    try {
      const r = await axios.post(`${API}/ai-import/analyze`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMessages((m) => m.map((x) => (x.id === msg.id
        ? { ...x, data: r.data, cache: { ...(x.cache || {}), [newTarget]: r.data } }
        : x)));
    } catch (e) {
      toast.error(e.response?.data?.detail || '解析に失敗しました');
    } finally {
      setBusy(false);
      setBusyStartedAt(null);
      setBusyLabel(null);
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setMessages((m) => [...m, { id: nextId(), role: 'user', text: f.name, size: f.size }]);
    analyze(f);
    e.target.value = '';
  };

  const downloadTemplate = async (target) => {
    try {
      const r = await axios.get(`${API}/ai-import/template/${target}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      const cd = r.headers['content-disposition'] || '';
      const mt = cd.match(/filename="?([^"]+)"?/);
      a.download = mt ? mt[1] : `template_${target}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (_e) {
      toast.error('テンプレートのダウンロードに失敗しました');
    }
  };

  const confirmImport = async (msg, deptId) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/ai-import/confirm`, {
        session_id: msg.data.session_id,
        target: msg.data.target,
        department_id: deptId || null,
      });
      setMessages((m) => m.map((x) => (x.id === msg.id ? { ...x, consumed: true } : x)));
      setMessages((m) => [...m, { id: nextId(), role: 'result', data: r.data }]);
      loadBatches();
      toast.success(`${r.data.target_label} を取り込みました`);
    } catch (e) {
      toast.error(e.response?.data?.detail || '取り込みに失敗しました');
    } finally {
      setBusy(false);
    }
  };

  const undo = async (batchId) => {
    if (!window.confirm('この取り込みを取り消しますか？\n追加された行は削除され、上書きされた行は元に戻ります。')) return;
    try {
      const r = await axios.post(`${API}/ai-import/batches/${batchId}/undo`);
      toast.success(`取り消しました（削除 ${r.data.deleted} / 復元 ${r.data.restored}）`);
      loadBatches();
    } catch (e) {
      toast.error(e.response?.data?.detail || '取り消しに失敗しました');
    }
  };

  const modelLabel = config?.model_label || 'AI';

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="ai-import-panel">
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2 shrink-0">
        <button type="button" onClick={onBack} data-testid="ai-import-back-btn"
          className="md:hidden p-1.5 -ml-1.5 rounded text-zinc-500 hover:bg-zinc-100 shrink-0" title="一覧へ戻る">
          <CaretLeft size={18} weight="bold" />
        </button>
        <span className="w-8 h-8 rounded-lg bg-teal-700 text-white flex items-center justify-center shrink-0">
          <Robot size={18} weight="fill" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-bold text-zinc-900 truncate">AI データ取り込み</p>
          <p className="text-[11px] text-zinc-400 truncate flex items-center gap-1">
            <Sparkle size={11} weight="fill" className="text-violet-500" />
            {modelLabel}
          </p>
        </div>
        <button type="button" onClick={() => setShowHistory((v) => !v)} data-testid="ai-import-history-toggle"
          className={`ml-auto inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg transition-colors ${
            showHistory ? 'bg-teal-700/10 text-teal-700' : 'text-zinc-500 hover:bg-zinc-100'
          }`}>
          <ClockCounterClockwise size={15} />
          履歴{batches.length > 0 ? `（${batches.length}）` : ''}
        </button>
      </div>

      {/* Body */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-zinc-50/40">
        {showHistory ? (
          <HistoryView batches={batches} onUndo={undo} />
        ) : (
          <>
            {/* Intro */}
            <BotBubble>
              <p className="font-semibold text-zinc-900">こんにちは！データ取り込みアシスタントです。</p>
              <p className="mt-1 text-zinc-600 leading-relaxed">
                <b>Excel (.xlsx) / CSV / 画像 (PNG・JPG・WebP) / PDF</b> ファイルをアップロードしてください。
                <b>{modelLabel}</b> が内容を解析し、
                <b>社員 / 部署 / 仕入先 / 品目 / 部品表(BOM) / 在庫(入荷) / 受注分類 / メーカー / 単位 / 材質</b> のどれに該当するかを推定して、
                取り込み前にプレビューを表示します。取り込み後もいつでも <b>取り消し</b> できます。
              </p>
              <p className="mt-1.5 text-[11px] text-zinc-400">
                ※ 画像・PDF は AI (Gemini) が表を読み取ります。表が鮮明に写った1枚を推奨。
              </p>
              <p className="mt-0.5 text-[11px] text-zinc-400">※ 部品表・在庫は部門ごとに管理されるため、取り込み時に部門の選択が必要です。</p>
              {config?.max_rows && (
                <p className="mt-0.5 text-[11px] text-zinc-400">※ 1ファイルあたり最大 {config.max_rows.toLocaleString()} 行まで。</p>
              )}
            </BotBubble>

            {messages.map((m) => {
              if (m.role === 'user') return <UserBubble key={m.id} name={m.text} size={m.size} />;
              if (m.role === 'error') return (
                <BotBubble key={m.id} tone="error">
                  <p className="flex items-center gap-1.5 font-medium text-rose-700"><Warning size={16} weight="fill" /> {m.text}</p>
                </BotBubble>
              );
              if (m.role === 'result') return <ResultBubble key={m.id} data={m.data} onUndo={undo} />;
              if (m.role === 'analysis') return (
                <AnalysisBubble key={m.id} msg={m} config={config} busy={busy} departments={config?.departments || []}
                  onConfirm={(deptId) => confirmImport(m, deptId)}
                  onChangeTarget={(t) => changeTarget(m, t)} />
              );
              return null;
            })}

            {busy && (
              <BotBubble>
                <div className="w-full">
                  <p className="flex items-center gap-2 text-zinc-500 mb-2"><CircleNotch size={16} className="animate-spin" /> 解析しています…</p>
                  <AiProgressBanner
                    steps={['ファイルを読み込み中…', 'AIで内容を解析中…', 'カラムをマッピング中…', 'プレビューを整形中…']}
                    since={busyStartedAt}
                    tone="violet"
                    defaultLabel={busyLabel || 'AIがファイルを解析しています…'}
                    footer="Excel/CSV/画像/PDFをAIが読み取り、取り込み可能な形にマッピングします（通常 20〜60 秒）。"
                    testIdPrefix="ai-import-progress"
                    simulatedStepMs={12000}
                  />
                </div>
              </BotBubble>
            )}
          </>
        )}
      </div>

      {/* Upload bar */}
      {!showHistory && (
        <div className="px-4 py-3 border-t border-zinc-100 shrink-0 bg-white">
          <input ref={fileRef} type="file"
            accept=".xlsx,.xlsm,.csv,.txt,.png,.jpg,.jpeg,.webp,.pdf,image/*,application/pdf"
            className="hidden"
            data-testid="ai-import-file-input" onChange={onFile} />
          <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="ai-import-upload-btn"
            className="w-full h-11 bg-teal-700 hover:from-violet-700 hover:to-indigo-700 text-white font-semibold">
            <FileArrowUp size={18} weight="bold" className="mr-2" />
            ファイルをアップロード（Excel / CSV / 画像 / PDF）
          </Button>
          <div className="mt-2 flex items-center gap-x-2 gap-y-1 flex-wrap justify-center text-[11px]">
            <span className="text-zinc-400 inline-flex items-center gap-1"><DownloadSimple size={12} /> テンプレート:</span>
            {(config?.targets || []).map((t) => (
              <button key={t.key} type="button" onClick={() => downloadTemplate(t.key)}
                data-testid={`ai-import-template-${t.key}`}
                className="text-violet-600 hover:text-violet-800 hover:underline font-medium">
                {t.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── sub-components ─────────────────────────────────────────────────────── */

function BotBubble({ children, tone }) {
  return (
    <div className="flex gap-2.5 max-w-[92%]">
      <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 text-white ${
        tone === 'error' ? 'bg-rose-500' : 'bg-teal-700'
      }`}>
        <Robot size={15} weight="fill" />
      </span>
      <div className="rounded-lg rounded-tl-sm bg-white border border-zinc-200 px-3.5 py-2.5 text-[13px] text-zinc-700 shadow-sm">
        {children}
      </div>
    </div>
  );
}

function UserBubble({ name, size }) {
  return (
    <div className="flex justify-end">
      <div className="rounded-lg rounded-tr-sm bg-teal-700 text-white px-3.5 py-2.5 text-[13px] max-w-[80%] shadow-sm">
        <p className="flex items-center gap-1.5 font-medium"><UploadSimple size={14} /> {name}</p>
        {size != null && <p className="text-[11px] text-blue-100 mt-0.5">{(size / 1024).toFixed(1)} KB</p>}
      </div>
    </div>
  );
}

function statusClass(s) {
  if (s === 'OK') return 'bg-emerald-50 text-emerald-700';
  return 'bg-amber-50 text-amber-700';
}

function AnalysisBubble({ msg, config, busy, departments, onConfirm, onChangeTarget }) {
  const [dept, setDept] = useState('');
  const d = msg.data;
  const targets = config?.targets || [];
  const columns = d.preview_columns || [];
  const reqDept = !!d.requires_department;
  const canConfirm = columns.length > 0 && d.preview?.length > 0 && (!reqDept || !!dept);

  return (
    <BotBubble>
      <div className="min-w-0" data-testid={`ai-import-analysis-${d.session_id}`}>
        <p className="text-zinc-800">
          {d.used_ai ? <span className="inline-flex items-center gap-1 text-violet-600 font-medium"><Sparkle size={13} weight="fill" /> AIが解析しました</span>
            : <span className="text-amber-600 font-medium">キーワードで推定しました</span>}
          。取り込み先を確認してください。
        </p>
        {d.reason && <p className="mt-1 text-[12px] text-zinc-500 italic">「{d.reason}」</p>}

        {/* target selector */}
        <div className="mt-2.5 flex items-center gap-2 flex-wrap">
          <span className="text-[12px] text-zinc-500">取り込み先：</span>
          <select
            value={d.target}
            disabled={busy || msg.consumed}
            onChange={(e) => onChangeTarget(e.target.value)}
            data-testid={`ai-import-target-select-${d.session_id}`}
            className="text-[13px] font-semibold border border-zinc-300 rounded-lg px-2.5 py-1.5 bg-white text-zinc-800 disabled:opacity-60"
          >
            {targets.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <span className="text-[12px] text-zinc-500">全 <b className="text-zinc-800">{d.total_rows}</b> 行</span>
        </div>

        {/* department selector (BOM / inventory) */}
        {reqDept && (
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className="text-[12px] text-rose-600 font-medium">部門（必須）：</span>
            <select
              value={dept}
              disabled={busy || msg.consumed}
              onChange={(e) => setDept(e.target.value)}
              data-testid={`ai-import-dept-select-${d.session_id}`}
              className="text-[13px] font-semibold border border-zinc-300 rounded-lg px-2.5 py-1.5 bg-white text-zinc-800 disabled:opacity-60"
            >
              <option value="">選択してください…</option>
              {departments.map((dep) => <option key={dep.id} value={dep.id}>{dep.name}</option>)}
            </select>
          </div>
        )}

        {/* summary */}
        {d.summary && <p className="mt-2 text-[12px] text-zinc-600 bg-zinc-50 border border-zinc-100 rounded-lg px-2.5 py-1.5">{d.summary}</p>}

        {/* warnings */}
        {(d.warnings || []).length > 0 && (
          <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-2 text-[12px] text-amber-800 space-y-1">
            {d.warnings.map((w, i) => (
              <p key={`${i}-${w}`} className="flex gap-1.5 items-start"><Warning size={14} weight="fill" className="shrink-0 mt-0.5" /><span>{w}</span></p>
            ))}
          </div>
        )}

        {/* preview table */}
        {columns.length > 0 && d.preview?.length > 0 ? (
          <div className="mt-2.5 border border-zinc-200 rounded-lg overflow-hidden">
            <div className="px-2.5 py-1.5 bg-zinc-50 border-b border-zinc-200 text-[11px] font-semibold text-zinc-500 flex items-center gap-1">
              <TableIcon size={13} /> プレビュー（先頭 {d.preview.length} 行）
            </div>
            <div className="overflow-x-auto max-h-56">
              <table className="w-full text-[12px]">
                <thead className="bg-zinc-50 sticky top-0">
                  <tr>
                    {columns.map((c) => (
                      <th key={c.key} className="text-left font-semibold text-zinc-600 px-2.5 py-1.5 whitespace-nowrap border-b border-zinc-200">
                        {c.label}{c.required && <span className="text-rose-500">*</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {d.preview.map((row, i) => (
                    <tr key={`${i}-${row[columns[0]?.key] ?? ''}`} className="odd:bg-white even:bg-zinc-50/60">
                      {columns.map((c) => (
                        <td key={c.key} className="px-2.5 py-1.5 text-zinc-700 whitespace-nowrap max-w-[180px] truncate">
                          {c.status ? (
                            <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${statusClass(row[c.key])}`}>{row[c.key]}</span>
                          ) : (row[c.key] === null || row[c.key] === '' ? <span className="text-zinc-300">—</span> : String(row[c.key]))}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <p className="mt-2 text-[12px] text-rose-600">対応付けできる列が見つかりませんでした。取り込み先を変更してみてください。</p>
        )}

        {/* action */}
        {msg.consumed ? (
          <p className="mt-2.5 text-[12px] text-emerald-600 flex items-center gap-1"><CheckCircle size={14} weight="fill" /> 取り込み済み</p>
        ) : (
          <div className="mt-2.5 flex items-center gap-2">
            <Button size="sm" onClick={() => onConfirm(dept)} disabled={busy || !canConfirm}
              data-testid={`ai-import-confirm-btn-${d.session_id}`}
              className="bg-teal-700 hover:bg-teal-800 text-white h-8">
              <CheckCircle size={15} weight="fill" className="mr-1" /> この内容で取り込む
            </Button>
            {reqDept && !dept && <span className="text-[11px] text-rose-500">部門を選択してください</span>}
          </div>
        )}
      </div>
    </BotBubble>
  );
}

function ResultBubble({ data, onUndo }) {
  return (
    <BotBubble>
      <div data-testid={`ai-import-result-${data.batch_id}`}>
        <p className="flex items-center gap-1.5 font-semibold text-emerald-700">
          <CheckCircle size={16} weight="fill" /> {data.target_label} を取り込みました
        </p>
        <div className="mt-1.5 flex items-center gap-3 text-[12px] text-zinc-600">
          <span>新規 <b className="text-emerald-700">{data.inserted}</b></span>
          <span>更新 <b className="text-blue-700">{data.updated}</b></span>
          <span>スキップ <b className="text-zinc-500">{data.skipped}</b></span>
        </div>
        <Button size="sm" variant="outline" onClick={() => onUndo(data.batch_id)}
          data-testid={`ai-import-undo-btn-${data.batch_id}`}
          className="mt-2.5 h-8 text-rose-600 border-rose-200 hover:bg-rose-50">
          <ArrowCounterClockwise size={15} className="mr-1" /> この取り込みを取り消す
        </Button>
      </div>
    </BotBubble>
  );
}

function HistoryView({ batches, onUndo }) {
  if (!batches.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <ClockCounterClockwise size={40} className="text-zinc-300 mb-2" />
        <p className="text-sm text-zinc-400">まだ取り込み履歴はありません。</p>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="ai-import-history">
      {batches.map((b) => {
        const undone = b.status === 'undone';
        return (
          <div key={b.id} className="bg-white border border-zinc-200 rounded-xl px-3.5 py-3 flex items-center gap-3 shadow-sm"
            data-testid={`ai-import-history-row-${b.id}`}>
            <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
              undone ? 'bg-zinc-100 text-zinc-400' : 'bg-emerald-50 text-emerald-600'
            }`}>
              <TableIcon size={17} weight="duotone" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-zinc-900 truncate">
                {b.label}
                {b.filename && <span className="font-normal text-zinc-400"> · {b.filename}</span>}
              </p>
              <p className="text-[11px] text-zinc-500">
                新規 {b.counts?.inserted ?? 0} / 更新 {b.counts?.updated ?? 0} / スキップ {b.counts?.skipped ?? 0}
                　{b.created_at ? formatDateTime(b.created_at) : ''}
              </p>
            </div>
            {undone ? (
              <span className="text-[11px] font-medium text-zinc-400 bg-zinc-100 rounded-full px-2.5 py-1 shrink-0">取消済み</span>
            ) : (
              <Button size="sm" variant="outline" onClick={() => onUndo(b.id)}
                data-testid={`ai-import-history-undo-${b.id}`}
                className="h-8 text-rose-600 border-rose-200 hover:bg-rose-50 shrink-0">
                <ArrowCounterClockwise size={14} className="mr-1" /> 取り消す
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}
